/**
 * AI Readiness Page
 *
 * Conversion page for the AI readiness offer — open layout aligned with homepage.
 *
 * Primary responsibilities:
 * - Build desire for the company readiness report
 * - Move visitors into the email-first submission flow
 *
 * Dependencies:
 * - AI readiness lead magnet section
 * - marketing primitives
 */

'use client'

import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

import { AiReadinessLeadMagnetSection } from '@/components/quick-ai-readiness/lead-magnet-section'
import { GeneratedMarketingAsset } from '@/components/marketing/generated-marketing-asset'
import {
  MarketingAssetOuterFade,
  marketingAssetSoftEdgeClassName
} from '@/components/marketing/marketing-asset-outer-fade'
import {
  MarketingContainer,
  MarketingEyebrow,
  MarketingHero,
  MarketingPage,
  MarketingSection
} from '@/components/marketing/marketing-primitives'
import { getMarketingCopy } from '@/lib/marketing-copy'

export default function AiReadinessPage() {
  const { i18n } = useTranslation()
  const copy = getMarketingCopy(i18n.resolvedLanguage || i18n.language)

  return (
    <MarketingPage>
      <MarketingContainer>
        <MarketingHero
          eyebrow={<MarketingEyebrow>{copy.aiReadiness.intro.eyebrow}</MarketingEyebrow>}
          title={copy.aiReadiness.intro.title}
          description={copy.aiReadiness.intro.description}
          aside={
            <div className={cn("relative isolate min-h-[300px] overflow-hidden sm:min-h-[360px] lg:min-h-[400px]", marketingAssetSoftEdgeClassName)}>
              <GeneratedMarketingAsset
                assetKey="aiReadinessAnalysis"
                frame={false}
                presentation="open"
                className="absolute inset-0"
                imageClassName="object-contain object-center opacity-95"
                sizes="(min-width: 1024px) 560px, 100vw"
              />
              <MarketingAssetOuterFade />
            </div>
          }
        />

        <div className="mt-10">
          <AiReadinessLeadMagnetSection source="ai_readiness_page" variant="page" />
        </div>

        <section className="relative mt-12 grid items-start gap-10 py-8 lg:mt-16 lg:grid-cols-[0.95fr_1.05fr] lg:gap-14 lg:py-12">
          <div className={cn("relative isolate min-h-[320px] overflow-hidden sm:min-h-[400px]", marketingAssetSoftEdgeClassName)}>
            <GeneratedMarketingAsset
              assetKey="aiReadinessReport"
              frame={false}
              presentation="open"
              className="absolute inset-0"
              imageClassName="object-contain object-center opacity-95"
              sizes="(min-width: 1024px) 420px, 100vw"
            />
            <MarketingAssetOuterFade />
          </div>

          <div className="flex max-w-[40rem] flex-col gap-5">
            <MarketingEyebrow>{copy.aiReadiness.expectations.eyebrow}</MarketingEyebrow>
            <h2 className="font-display text-4xl font-medium leading-[0.98] tracking-[-0.02em] text-slate-950 sm:text-5xl dark:text-slate-50">
              {copy.aiReadiness.expectations.title}
            </h2>
            <p className="text-base leading-8 text-slate-600 dark:text-slate-300">
              {copy.aiReadiness.expectations.description}
            </p>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
              {copy.aiReadiness.expectations.panelLabel}
            </p>
            <h3 className="text-2xl font-semibold tracking-tight text-slate-950 dark:text-slate-50">
              {copy.aiReadiness.expectations.panelTitle}
            </h3>
            <div className="grid gap-4 text-sm leading-7 text-slate-600 dark:text-slate-300">
              {copy.aiReadiness.expectations.body.map(paragraph => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </div>
        </section>

        <MarketingSection
          eyebrow={<MarketingEyebrow>{copy.aiReadiness.faqLabel}</MarketingEyebrow>}
          title={copy.aiReadiness.faqLabel}
          contentClassName="mx-auto max-w-[52rem]"
        >
          <ol>
            {copy.aiReadiness.faqs.map(item => (
              <li
                key={item.question}
                className="border-t border-slate-200/80 py-7 first:border-t-0 first:pt-0 dark:border-white/10"
              >
                <h3 className="text-xl font-semibold tracking-tight text-slate-950 dark:text-slate-50">
                  {item.question}
                </h3>
                <p className="mt-2 max-w-[42ch] text-sm leading-7 text-slate-600 dark:text-slate-300">
                  {item.answer}
                </p>
              </li>
            ))}
          </ol>
        </MarketingSection>
      </MarketingContainer>
    </MarketingPage>
  )
}
